
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, MapPin } from "lucide-react";
import { useStaffLocations } from "@/hooks/useStaffLocations";

interface LocationAssignmentDialogProps {
  staffId: string;
  staffName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LocationAssignmentDialog: React.FC<LocationAssignmentDialogProps> = ({
  staffId,
  staffName,
  open,
  onOpenChange,
}) => {
  const {
    assignments,
    isLoading,
    isUpdating,
    toggleLocationAssignment
  } = useStaffLocations(staffId);

  const handleCheckboxChange = (locationId: string, isChecked: boolean) => {
    toggleLocationAssignment(locationId, !isChecked);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location Assignments for {staffName}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              No locations available. Please add locations in the Settings page.
            </div>
          ) : (
            <div className="space-y-4">
              {assignments.map((assignment) => (
                <div key={assignment.location_id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`location-${assignment.location_id}`}
                    checked={assignment.assigned}
                    disabled={isUpdating}
                    onCheckedChange={(checked) => 
                      handleCheckboxChange(assignment.location_id, !!checked)
                    }
                  />
                  <label
                    htmlFor={`location-${assignment.location_id}`}
                    className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {assignment.location_name}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LocationAssignmentDialog;
